/**
 * Intercom messenger.
 *
 * Mirrors the behaviour of livepush.io and the blog: any element carrying
 * `data-intercom-message` opens the messenger with that text pre-filled, falling back to
 * its href if the widget never loads. Page views are reported as `visited_page`.
 *
 * The widget is loaded lazily — on first interaction, or when the browser goes idle —
 * so it never competes with first paint on a docs page.
 */

declare global {
  interface Window {
    Intercom?: ((...args: unknown[]) => void) & { q?: unknown[]; c?: (a: unknown) => void };
    intercomSettings?: Record<string, unknown>;
    openChat?: (message?: string, fallbackUrl?: string) => void;
  }
}

/** Injected as <meta name="intercom-app-id"> from site.config.ts at build time. */
const APP_ID =
  document.querySelector<HTMLMetaElement>('meta[name="intercom-app-id"]')?.content ?? '';

let loading = false;

function loadWidget(): void {
  if (loading || !APP_ID) return;
  loading = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${APP_ID}`;
  document.head.appendChild(script);
}

/** Stubs window.Intercom so calls made before the script lands are replayed after. */
function installStub(): void {
  if (typeof window.Intercom === 'function') return;

  const queue: unknown[] = [];
  const stub = ((...args: unknown[]) => {
    queue.push(args);
  }) as NonNullable<Window['Intercom']>;
  stub.q = queue;
  stub.c = (args: unknown) => queue.push(args);
  window.Intercom = stub;
}

function openChat(message?: string, fallbackUrl?: string): void {
  loadWidget();

  let attempts = 0;
  const open = () => {
    // Once the real widget replaces the stub it exposes `booted`; until then, queue.
    if (typeof window.Intercom === 'function' && !window.Intercom.q) {
      window.Intercom(message ? 'showNewMessage' : 'show', ...(message ? [message] : []));
      return;
    }
    attempts += 1;
    if (attempts < 16) {
      setTimeout(open, 300);
      return;
    }
    if (fallbackUrl) window.location.href = fallbackUrl;
  };
  open();
}

export function initIntercom(): void {
  if (!APP_ID) return;

  window.intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: APP_ID,
  };
  installStub();
  window.openChat = openChat;

  document.addEventListener('click', (event) => {
    const el = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-intercom-message]',
    );
    if (!el) return;
    event.preventDefault();
    openChat(el.getAttribute('data-intercom-message') ?? undefined, el.getAttribute('href') ?? undefined);
  });

  const kick = () => {
    loadWidget();
    window.Intercom?.('trackEvent', 'visited_page', { page: window.location.href });
  };

  const once = { once: true, passive: true } as const;
  for (const evt of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(evt, kick, once);
  }

  if ('requestIdleCallback' in window) {
    (window as unknown as {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(kick, { timeout: 3500 });
  } else {
    setTimeout(kick, 2500);
  }
}
